import type { ReactNode } from 'react'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import { ChevronDown } from 'lucide-react'

import {
  collapseChevronTransitionClass,
  collapseEnterTransition,
  collapseExitTransition,
} from '@/lib/animation-variants'
import { cn } from '@/lib/utils'

interface AnimatedCollapseProps {
  open: boolean
  children: ReactNode
  className?: string
}

/**
 * Renders `children` while `open`, animating height 0 ↔ auto with a light
 * opacity fade. Only the toggle animates (`initial={false}`), so content that
 * starts open simply appears. Overflow is clipped only while animating, so
 * focus rings and popovers inside are not cut off once settled. Under reduced
 * motion it is a plain conditional render.
 *
 * Keep padding, margins and borders on the children, not on `className`:
 * the wrapper itself animates to height 0, and its own box would not shrink.
 */
export function AnimatedCollapse({ open, children, className }: AnimatedCollapseProps) {
  const prefersReducedMotion = useReducedMotion()

  if (prefersReducedMotion) {
    return open ? <div className={className}>{children}</div> : null
  }

  return (
    <AnimatePresence initial={false}>
      {open && (
        <motion.div
          key="animated-collapse"
          className={className}
          initial={{ height: 0, opacity: 0, overflow: 'hidden' }}
          animate={{
            height: 'auto',
            opacity: 1,
            transition: collapseEnterTransition,
            transitionEnd: { overflow: 'visible' },
          }}
          exit={{ height: 0, opacity: 0, overflow: 'hidden', transition: collapseExitTransition }}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  )
}

/**
 * AnimatePresence for pieces that swap alongside an {@link AnimatedCollapse}
 * (header summaries, captions). Under reduced motion it drops out entirely,
 * so leaving elements unmount at once instead of waiting on an exit.
 * Pair the children with {@link useCollapseFade}.
 */
export function CollapsePresence({ children }: { children: ReactNode }) {
  const prefersReducedMotion = useReducedMotion()
  if (prefersReducedMotion) return <>{children}</>
  return <AnimatePresence initial={false}>{children}</AnimatePresence>
}

interface CollapseSwapOptions {
  /**
   * Hold the enter until a sibling's exit has finished, so one header piece
   * leaves before the next arrives instead of the two overlapping.
   */
  afterExit?: boolean
}

function enterTransition({ afterExit }: CollapseSwapOptions) {
  return afterExit ? { ...collapseEnterTransition, delay: collapseExitTransition.duration } : collapseEnterTransition
}

/** Opacity fade props on the collapse timing; no animation under reduced motion. */
export function useCollapseFade(options: CollapseSwapOptions = {}) {
  const prefersReducedMotion = useReducedMotion()
  if (prefersReducedMotion) return { initial: false as const }
  return {
    initial: { opacity: 0 },
    animate: { opacity: 1, transition: enterTransition(options) },
    exit: { opacity: 0, transition: collapseExitTransition },
  }
}

/**
 * Inline width + fade on the collapse timing, for a header piece whose
 * neighbours should glide into the space it leaves rather than snap. The
 * element clips while animating and stops clipping once settled; give it
 * `inline-block whitespace-nowrap` and put any gap on its inside (padding),
 * since a parent gap would still jump when it unmounts.
 */
export function useCollapseWidth(options: CollapseSwapOptions = {}) {
  const prefersReducedMotion = useReducedMotion()
  if (prefersReducedMotion) return { initial: false as const }
  return {
    initial: { width: 0, opacity: 0, overflow: 'hidden' },
    animate: {
      width: 'auto',
      opacity: 1,
      transition: enterTransition(options),
      transitionEnd: { overflow: 'visible' },
    },
    exit: { width: 0, opacity: 0, overflow: 'hidden', transition: collapseExitTransition },
  }
}

/** Disclosure chevron: one ChevronDown that turns 180° when open. */
export function CollapseChevron({ open, className }: { open: boolean; className?: string }) {
  return <ChevronDown aria-hidden className={cn(collapseChevronTransitionClass, open && 'rotate-180', className)} />
}
